import { useMemo, type ReactNode } from "react";
import { DocCopyField, DocSegLegend, DocTerm, DocTimeline, type DocBeat } from "./doc/DocInteractive";
import { DocCalc, parseCalcRange, type DocCalcAudience, type DocCalcTariff } from "./doc/DocCalc";
import { DocCampaignMap, DocEmailCard, DocRef, type DocMapPhase, type DocMapRow } from "./doc/DocEmail";
import {
  DocArc, DocEveningBlock, DocFunnel, DocMinutes, DocScope, DocSeam, DocWfForm, DocWireframe,
  minuteLabel, parseMinutes,
  type DocArcMove, type DocEveningPhase, type DocFunnelStage, type DocMinuteRow
} from "./doc/DocWebinar";
import { docContextFromNodes, type DocContext } from "./doc/doc-context";
import { docT } from "./doc/doc-locale";
import { docNodesToPlainText } from "./doc/doc-plain";
import {
  childrenOf, docText, emailCardId, isDocHtml, isInlineOnly, parseDocHtml, phaseCardId, safeDocId,
  type DocAttrs, type DocNode
} from "./doc/doc-parse";
import { segmentClass, segmentDef, segmentKey, type SegmentDef, type SegmentMap } from "./doc/doc-segments";

/**
 * Authored-HTML documents (Pavel 2026-08-03; interactive since 2026-08-06).
 *
 * Markdown gave the strategy documents no control over LAYOUT: every block was
 * a full-width row in one vertical stack, so a document could only ever be a
 * scroll, never a composition. Two columns, card grids, pulled-out asides and
 * a persona portrait were impossible without inventing a new `:::` fence for
 * each one.
 *
 * So the builder authors documents in a small HTML component language. Four
 * properties matter and none of them is negotiable:
 *
 * 1. NOTHING is injected. We never touch dangerouslySetInnerHTML. The source
 *    is parsed into a tree and only whitelisted tags become React elements;
 *    an unknown tag renders its children as plain content. A model that emits
 *    `<script>` or an onclick attribute produces text, not behaviour. The
 *    interactive tags are no exception: `<term>`, `<copyfield>` and
 *    `<timeline>` are components THIS renderer decides to place, and their
 *    behaviour is written here, not in the document.
 * 2. The rendered DOM keeps the reader's contract. Chapters stay
 *    `h3.doc-section` (the chapter rail and reading progress query them),
 *    and every leaf stays one of the anchorable elements/classes the walk
 *    annotation uses, so "Mluv a ukazuj" keeps working on the new layouts.
 * 3. Markdown documents keep rendering unchanged. Every document delivered
 *    before 2026-08-03 is markdown; `isDocHtml` decides per document.
 * 4. A control that cannot do its job is not drawn as a control. A timeline
 *    beat whose fold does not exist renders as text, never as a button that
 *    scrolls nowhere.
 *
 * The parser lives in `doc/doc-parse.ts` (the plain-text conversion and the
 * segment pass need a tree without importing a component) and is re-exported
 * here, because every existing call site imports it from this module.
 */

export type { DocAttrs, DocNode };
export { docText, isDocHtml, parseDocHtml };

/** `value | note` rows come back as a clamped 0-100 bar width. */
export function meterWidth(value: string): number | null {
  const match = /-?\d+(?:[.,]\d+)?/.exec(value);
  if (!match) return null;
  const parsed = Number(match[0].replace(",", "."));
  if (!Number.isFinite(parsed)) return null;
  return Math.max(0, Math.min(100, parsed));
}

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function renderInline(nodes: DocNode[], ctx: RenderCtx, keyBase: string): ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${keyBase}-${index}`;
    if (node.kind === "text") return <span key={key}>{node.text}</span>;
    const inner = renderInline(node.children, ctx, key);
    switch (node.tag) {
      case "b":
      case "strong":
        return <strong key={key}>{inner}</strong>;
      case "i":
      case "em":
        return <em key={key}>{inner}</em>;
      case "mark":
        return <mark key={key} className="doc-mark">{inner}</mark>;
      case "code":
        return <code key={key}>{inner}</code>;
      case "br":
        return <br key={key} />;

      // A word the reader may not know, explained on hover, on focus and on
      // tap. `<term seg="S1"/>` with no body prints the segment's own label and
      // borrows its explanation, so a document defines a segment once and then
      // names it anywhere: exactly what Pavel asked for when he hovered
      // "segment Seznam" and nothing happened.
      case "term": {
        const def = segmentDef(ctx.segments, node.attrs.seg);
        const tip = node.attrs.tip ?? def?.tip ?? "";
        const body = node.children.length > 0 ? inner : def?.label ?? "";
        return (
          <DocTerm key={key} tip={tip} segClass={segmentClass(ctx.segments, node.attrs.seg)}>
            {body}
          </DocTerm>
        );
      }

      // A field the reader has to fill in before sending. Coloured and bold,
      // so an unfilled one cannot be read past. `kind="merge"` is the MAIL
      // TOOL's field instead (R4): neutral and code-like, so the client
      // learns this one is not theirs to fill by hand.
      case "gap": {
        const merge = node.attrs.kind === "merge";
        return <span key={key} className={merge ? "doc-gap doc-gap-merge" : "doc-gap"}>{inner}</span>;
      }

      // Inline jump into the document (R3): the pre-flight checklist points
      // back at the e-mail a gap lives in. Same law as the timeline: a ref
      // whose target does not exist keeps its words and offers no control.
      case "ref": {
        const target = safeDocId(node.attrs.to);
        return (
          <DocRef key={key} target={target && ctx.foldIds.has(target) ? target : null}>
            {inner}
          </DocRef>
        );
      }

      default:
        return <span key={key}>{inner}</span>;
    }
  });
}

/**
 * The auto chip's text: `auto="odchází sám při registraci"` prints the phrase,
 * a bare flag (`auto`, `auto="1"`) falls back to the generic label.
 */
function autoChipLabel(raw: string | undefined): string | undefined {
  if (raw === undefined) return undefined;
  const value = raw.trim();
  if (value.toLowerCase() === "false") return undefined;
  return value && !["1", "true"].includes(value.toLowerCase()) ? value : docT("doc.email.auto");
}

interface RenderCtx {
  /** Chapters are numbered by the renderer, exactly as markdown `##` was. */
  chapter: { n: number };
  /** Segment colours and explanations, valid for the WHOLE document. */
  segments: SegmentMap;
  /** Fold ids that exist in the whole document (see doc/doc-context.ts). */
  foldIds: ReadonlySet<string>;
}

function renderNodes(nodes: DocNode[], ctx: RenderCtx, keyBase: string): ReactNode[] {
  const out: ReactNode[] = [];
  nodes.forEach((node, index) => {
    const rendered = renderNode(node, ctx, `${keyBase}-${index}`);
    if (rendered !== null) out.push(rendered);
  });
  return out;
}

/** Body of a container: inline-only content becomes a paragraph so it stays anchorable. */
function renderBody(node: DocNode, ctx: RenderCtx, key: string): ReactNode {
  if (node.kind !== "el") return null;
  if (node.children.length === 0) return null;
  if (isInlineOnly(node.children)) return <p key={`${key}-p`}>{renderInline(node.children, ctx, key)}</p>;
  return <>{renderNodes(node.children, ctx, key)}</>;
}

function renderNode(node: DocNode, ctx: RenderCtx, key: string): ReactNode {
  if (node.kind === "text") {
    return node.text.trim() ? <p key={key}>{node.text}</p> : null;
  }
  const { attrs } = node;
  switch (node.tag) {
    case "doc":
      return <div key={key} className="doc-html">{renderNodes(node.children, ctx, key)}</div>;

    case "lead":
      return <p key={key} className="doc-lead">{renderInline(node.children, ctx, key)}</p>;

    case "chapter": {
      ctx.chapter.n += 1;
      const n = ctx.chapter.n;
      return (
        <section key={key} className="doc-chapter">
          {/* h3.doc-section is the reader's contract: the chapter rail, the
              reading progress and the jump-to menu all query this selector.
              The numeral is decorative (the rail carries its own numbers). */}
          <h3 className="doc-section">
            <span className="doc-section-num" aria-hidden="true">{String(n).padStart(2, "0")}</span>
            <span className="doc-section-title">{attrs.title ?? ""}</span>
          </h3>
          {/* Minto's action-title move, adapted: the opener may carry the
              chapter's takeaway as one bold sentence, so a reader skimming
              openers alone gets the whole argument. */}
          {attrs.takeaway && <p className="doc-chapter-takeaway">{attrs.takeaway}</p>}
          {renderNodes(node.children, ctx, key)}
        </section>
      );
    }

    case "punch":
      // The document's ONE display pull-quote (typically the campaign
      // sentence). Cabinet Grotesk display scale - the second of the two
      // display peaks the brand allows per long surface.
      return <p key={key} className="doc-punch">{renderInline(node.children, ctx, key)}</p>;

    case "figure": {
      // Images only from our own origin (the avatar/preview endpoints).
      // A relative path is the whole contract; anything absolute or
      // protocol-relative is dropped while the caption survives.
      const src = attrs.src ?? "";
      const safe = src.startsWith("/") && !src.startsWith("//");
      return (
        <figure key={key} className="doc-figure" data-frame={attrs.frame ?? "default"}>
          {safe && <img src={src} alt={attrs.alt ?? attrs.caption ?? ""} loading="lazy" />}
          {attrs.caption && <figcaption>{attrs.caption}</figcaption>}
        </figure>
      );
    }

    case "h":
      return <h4 key={key} className="doc-subhead">{renderInline(node.children, ctx, key)}</h4>;

    case "p":
      return <p key={key}>{renderInline(node.children, ctx, key)}</p>;

    case "split": {
      const ratio = /^\d+:\d+$/.test(attrs.ratio ?? "") ? attrs.ratio : "1:1";
      return (
        <div key={key} className="doc-split" data-ratio={ratio}>
          {renderNodes(node.children, ctx, key)}
        </div>
      );
    }

    case "col":
      return <div key={key} className="doc-col">{renderNodes(node.children, ctx, key)}</div>;

    case "grid": {
      const cols = ["2", "3", "4"].includes(attrs.cols ?? "") ? attrs.cols : "2";
      return (
        <div key={key} className="doc-grid" data-cols={cols}>
          {renderNodes(node.children, ctx, key)}
        </div>
      );
    }

    case "card": {
      const cardSeg = segmentClass(ctx.segments, attrs.seg);
      return (
        <div key={key} className={["doc-card", cardSeg].filter(Boolean).join(" ")}>
          {/* A card that carries a segment prints its title AS the segment's
              chip (blueprint 4.4): the definition cards in "Komu píšeme" are
              where the reader learns which colour means whom. No tooltip -
              the explanation is the card body right below. */}
          {attrs.title && (
            <p className="doc-card-title">
              {cardSeg ? <span className={`${cardSeg} doc-seg-chip`}>{attrs.title}</span> : attrs.title}
            </p>
          )}
          {renderBody(node, ctx, key)}
        </div>
      );
    }

    case "aside": {
      // Map the authoring words onto the callout classes the stylesheet already
      // defines, so asides inherit the existing look instead of a second one.
      const KINDS: Record<string, string> = { klic: "key", pozor: "warn", tip: "tip", ok: "ok" };
      const kind = KINDS[attrs.kind ?? ""] ?? "key";
      return (
        <div key={key} className={`doc-callout doc-callout-${kind}`} data-callout={kind}>
          {renderBody(node, ctx, key)}
        </div>
      );
    }

    case "stats":
      return (
        <div key={key} className="doc-stats">
          {childrenOf(node, "stat").map((stat, index) => (
            <div key={`${key}-s${index}`} className="doc-stat">
              <span className="doc-stat-label">{stat.attrs.label ?? ""}</span>
              <span className="doc-stat-value" data-long={(stat.attrs.value ?? "").length > 14 ? "" : undefined}>{stat.attrs.value ?? ""}</span>
              {stat.attrs.note && <span className="doc-stat-note">{stat.attrs.note}</span>}
            </div>
          ))}
        </div>
      );

    case "hero":
      return (
        <div key={key} className="doc-hero">
          <span className="doc-hero-value">{attrs.value ?? ""}</span>
          <span className="doc-hero-claim">{attrs.claim ?? ""}</span>
          {attrs.note && <span className="doc-hero-note">{attrs.note}</span>}
        </div>
      );

    case "compare":
      return (
        <div key={key} className="doc-compare">
          <div className="doc-compare-side doc-compare-neg">
            <p className="doc-compare-title">{attrs.left ?? ""}</p>
            <ul>
              {childrenOf(node, "neg").map((item, index) => (
                <li key={`${key}-n${index}`}>{renderInline(item.children, ctx, `${key}-n${index}`)}</li>
              ))}
            </ul>
          </div>
          <div className="doc-compare-side doc-compare-pos">
            <p className="doc-compare-title">{attrs.right ?? ""}</p>
            <ul>
              {childrenOf(node, "pos").map((item, index) => (
                <li key={`${key}-p${index}`}>{renderInline(item.children, ctx, `${key}-p${index}`)}</li>
              ))}
            </ul>
          </div>
        </div>
      );

    case "steps":
      return (
        <ol key={key} className="doc-steps">
          {childrenOf(node, "step").map((step, index) => (
            <li key={`${key}-t${index}`} className="doc-step">
              {/* The ring marker already numbers the step; a label that is
                  just the same digit would print it twice. */}
              {step.attrs.label && step.attrs.label.trim() !== String(index + 1) && (
                <span className="doc-step-label">{step.attrs.label}</span>
              )}
              <span className="doc-step-title">{step.attrs.title ?? ""}</span>
              {(step.attrs.detail || step.children.length > 0) && (
                <span className="doc-step-detail">
                  {step.attrs.detail ?? docText(step.children)}
                </span>
              )}
            </li>
          ))}
        </ol>
      );

    case "meters":
      return (
        <div key={key} className="doc-meters">
          {childrenOf(node, "meter").map((meter, index) => {
            const width = meterWidth(meter.attrs.value ?? "");
            return (
              <div key={`${key}-m${index}`} className="doc-meter">
                <span className="doc-meter-label">{meter.attrs.label ?? ""}</span>
                <span className="doc-meter-value">{meter.attrs.value ?? ""}</span>
                {width !== null && (
                  <span className="doc-meter-bar" aria-hidden="true">
                    <span className="doc-meter-fill" style={{ width: `${width}%` }} />
                  </span>
                )}
                {meter.attrs.note && <span className="doc-meter-note">{meter.attrs.note}</span>}
              </div>
            );
          })}
        </div>
      );

    case "palette":
      return (
        <div key={key} className="doc-palette">
          {childrenOf(node, "swatch").map((swatch, index) => {
            const hex = swatch.attrs.hex ?? "";
            const valid = HEX.test(hex);
            return (
              <div key={`${key}-w${index}`} className="doc-swatch">
                {/* A non-hex value degrades to text rather than painting an
                    arbitrary string into a style attribute. */}
                <span
                  className="doc-swatch-chip"
                  style={valid ? { background: hex } : undefined}
                  data-empty={valid ? undefined : ""}
                  aria-hidden="true"
                />
                <span className="doc-swatch-name">{swatch.attrs.name ?? ""}</span>
                <span className="doc-swatch-hex">{hex}</span>
                {swatch.attrs.use && <span className="doc-swatch-note">{swatch.attrs.use}</span>}
              </div>
            );
          })}
        </div>
      );

    case "portrait":
      return (
        <figure key={key} className="doc-portrait">
          {attrs.name && <figcaption className="doc-portrait-name">{attrs.name}</figcaption>}
          {renderNodes(node.children, ctx, key)}
        </figure>
      );

    case "scene":
      return <p key={key} className="doc-scene">{renderInline(node.children, ctx, key)}</p>;

    case "quotes":
      return (
        <div key={key} className="doc-quotes">
          {childrenOf(node, "q").map((quote, index) => (
            <blockquote key={`${key}-q${index}`} className="doc-quote-short">
              {renderInline(quote.children, ctx, `${key}-q${index}`)}
            </blockquote>
          ))}
        </div>
      );

    case "quote":
      return (
        <blockquote key={key}>
          {renderBody(node, ctx, key)}
          {attrs.cite && <footer className="doc-quote-cite">{attrs.cite}</footer>}
        </blockquote>
      );

    // `id` is what a timeline beat scrolls to, so it is sanitized rather than
    // trusted: an id is a DOM selector, and the source is model output.
    case "fold":
      return (
        <details
          key={key}
          id={safeDocId(attrs.id)}
          className={["doc-fold", segmentClass(ctx.segments, attrs.seg)].filter(Boolean).join(" ")}
        >
          <summary>{attrs.title ?? ""}</summary>
          <div className="doc-fold-body">{renderNodes(node.children, ctx, key)}</div>
        </details>
      );

    // The subject, the alternate subjects and the body: every value the client
    // takes away, in a field that SAYS it can be taken (Pavel 2026-08-06:
    // "mělo by bejt v takovým jako tom poli, z kterýho je jasný, že to jde
    // zkopírovat... Vlastně takhle to splývá"). The clipboard text comes from
    // the same conversion the e-mail copy buttons use, so the two can never
    // disagree about a single word.
    case "copyfield": {
      const multiline = !isInlineOnly(node.children);
      return (
        <DocCopyField
          key={key}
          label={attrs.label}
          plain={docNodesToPlainText(node.children, ctx.segments)}
          multiline={multiline}
          segClass={segmentClass(ctx.segments, attrs.seg)}
        >
          {multiline ? renderNodes(node.children, ctx, key) : <p>{renderInline(node.children, ctx, key)}</p>}
        </DocCopyField>
      );
    }

    // R1 (LAYOUT-BLUEPRINT-EMAILS 4.1): the e-mail is ONE semantic tag and the
    // renderer owns the whole card. Composing it by hand from fold plus copy
    // fields in every generated document is exactly how the split-metadata bug
    // happened; the tag makes the correct layout the only expressible one.
    case "email": {
      const def = segmentDef(ctx.segments, attrs.seg);
      const subject = childrenOf(node, "subject")[0];
      const alts = childrenOf(node, "alt");
      const preheader = childrenOf(node, "preheader")[0];
      const body = childrenOf(node, "body")[0];
      const known = new Set(["subject", "alt", "preheader", "body"]);
      // Anything else the model wrote inside the e-mail keeps its words: a
      // loose paragraph must not vanish just because the card did not expect it.
      const rest = node.children.filter((child) => child.kind !== "el" || !known.has(child.tag));
      // F90/8 (Pavel 2026-08-07): the subject and the body ARE the e-mail,
      // the alternates and the inbox preview are metadata about it. Four
      // identical boxes read as one list ("takhle to splývá"), so the card
      // says which is which: `main` fields carry the warm content voice,
      // `meta` fields recede (doc-email.css owns both looks).
      const field = (
        source: Extract<DocNode, { kind: "el" }>,
        label: ReactNode,
        fieldKey: string,
        multiline: boolean,
        tone: "main" | "meta"
      ) => (
        <DocCopyField
          key={fieldKey}
          label={label}
          plain={docNodesToPlainText(source.children, ctx.segments)}
          multiline={multiline}
          className={`doc-email-field-${tone}`}
        >
          {multiline
            ? <>{renderNodes(source.children, ctx, fieldKey)}</>
            : <p>{renderInline(source.children, ctx, fieldKey)}</p>}
        </DocCopyField>
      );
      return (
        <DocEmailCard
          key={key}
          id={emailCardId(attrs.n)}
          n={(attrs.n ?? "").trim()}
          name={attrs.name ?? ""}
          day={attrs.day}
          time={attrs.time}
          autoLabel={autoChipLabel(attrs.auto)}
          task={attrs.task}
          segClass={segmentClass(ctx.segments, attrs.seg)}
          segLabel={def?.label}
          segTip={def?.tip}
        >
          {/* Open body order is the blueprint's: subject, alternates, inbox
              preview (its label explains itself, so "two subjects" can never
              come back), then the WHOLE body as one field. */}
          {subject && field(subject, docT("doc.email.subject"), `${key}-subj`, false, "main")}
          {alts.map((alt, index) => field(alt, docT("doc.email.altSubject"), `${key}-alt${index}`, false, "meta"))}
          {preheader && field(
            preheader,
            <DocTerm tip={docT("doc.email.preheaderTip")}>{docT("doc.email.preheader")}</DocTerm>,
            `${key}-pre`,
            false,
            "meta"
          )}
          {body && field(body, docT("doc.email.body"), `${key}-body`, !isInlineOnly(body.children), "main")}
          {rest.length > 0 && renderNodes(rest, ctx, `${key}-rest`)}
        </DocEmailCard>
      );
    }

    // Only meaningful inside an <email> or a <timeline>; strays keep their
    // words, exactly like a stray <beat> always has.
    case "subject": case "alt": case "preheader": case "body": case "phase": case "tl":
      if (node.children.length === 0) return null;
      return isInlineOnly(node.children)
        ? <p key={key}>{renderInline(node.children, ctx, key)}</p>
        : <>{renderNodes(node.children, ctx, key)}</>;

    // The legend that introduces the segments and hands out their colours.
    case "segments": {
      const items = childrenOf(node, "seg")
        .map((seg) => segmentDef(ctx.segments, seg.attrs.name))
        .filter((def): def is SegmentDef => def !== undefined);
      return <DocSegLegend key={key} title={attrs.title} items={items} />;
    }

    // A declaration on its own is DATA: it coloured the document during the
    // pre-pass and renders nothing here.
    case "seg":
      return null;

    case "timeline": {
      // R2 (blueprint 4.2): the <phase>/<tl> content model draws the vertical
      // campaign map keyed to <email n> cards; the flat <beat> form keeps the
      // horizontal axis it always had. Same dead-control law on both: a row
      // whose card does not exist is text, never a button.
      const phaseNodes = childrenOf(node, "phase");
      const looseRows = childrenOf(node, "tl");
      if (phaseNodes.length > 0 || looseRows.length > 0) {
        const toRow = (tl: Extract<DocNode, { kind: "el" }>): DocMapRow => {
          const target = emailCardId(tl.attrs.n);
          const rowDef = segmentDef(ctx.segments, tl.attrs.seg);
          return {
            n: (tl.attrs.n ?? "").trim(),
            day: tl.attrs.day ?? "",
            name: tl.attrs.name ?? "",
            auto: autoChipLabel(tl.attrs.auto) ?? null,
            target: target && ctx.foldIds.has(target) ? target : null,
            segClass: segmentClass(ctx.segments, tl.attrs.seg),
            segLabel: rowDef?.label,
            segTip: rowDef?.tip
          };
        };
        const phases: DocMapPhase[] = phaseNodes.map((phase) => ({
          label: phase.attrs.label ?? "",
          note: phase.attrs.note,
          rows: childrenOf(phase, "tl").map(toRow)
        }));
        // Rows a model left outside any phase still belong on the map.
        if (looseRows.length > 0) phases.push({ label: "", rows: looseRows.map(toRow) });
        if (phases.every((phase) => phase.rows.length === 0)) return null;
        return <DocCampaignMap key={key} caption={attrs.caption ?? attrs.title} phases={phases} />;
      }
      let previousSeg = "";
      const beats: DocBeat[] = childrenOf(node, "beat").map((beat) => {
        const target = safeDocId(beat.attrs.to);
        const def = segmentDef(ctx.segments, beat.attrs.seg);
        // The segment name is printed where the audience CHANGES HANDS, not on
        // every beat: five identical chips down one axis are noise, and the
        // one fact the axis can show that a list cannot is where the campaign
        // stops talking to one group and starts talking to another. The colour
        // carries the rest.
        const segKey = segmentKey(beat.attrs.seg);
        const changed = Boolean(segKey) && segKey !== previousSeg;
        previousSeg = segKey || previousSeg;
        return {
          day: beat.attrs.day ?? "",
          label: beat.attrs.label ?? "",
          note: beat.attrs.note ?? docText(beat.children),
          // A beat whose fold does not exist is not a button. This is the
          // renderer half of Pavel's "první e-mail nejde rozkliknout": the
          // document is still wrong, but it never offers a dead control.
          target: target && ctx.foldIds.has(target) ? target : null,
          segClass: segmentClass(ctx.segments, beat.attrs.seg),
          segLabel: changed && def?.declared ? def.label : undefined,
          segTip: def?.tip,
          now: beat.attrs.now !== undefined && beat.attrs.now !== "false"
        };
      });
      if (beats.length === 0) return null;
      return <DocTimeline key={key} title={attrs.title} beats={beats} />;
    }

    case "beat":
      // Only meaningful inside <timeline>; a stray one keeps its words.
      return isInlineOnly(node.children) && node.children.length > 0
        ? <p key={key}>{renderInline(node.children, ctx, key)}</p>
        : null;

    // F91: the webinar funnel - the customer's path as page cards on a
    // falling axis, each with its GOAL and its target number. A stage whose
    // `to` names no known id renders as a plain card, never a dead button.
    case "funnel": {
      const stages: DocFunnelStage[] = childrenOf(node, "fstage").map((stage, index) => {
        const target = safeDocId(stage.attrs.to);
        return {
          kind: stage.attrs.kind ?? "reg",
          name: stage.attrs.name ?? "",
          goal: stage.attrs.goal ?? "",
          num: stage.attrs.num ?? "",
          note: stage.attrs.note ?? "",
          target: target && ctx.foldIds.has(target) ? target : null,
          hasBody: stage.children.length > 0,
          body: renderInline(stage.children, ctx, `${key}-f${index}`)
        };
      });
      if (stages.length === 0) return null;
      return <DocFunnel key={key} title={attrs.title} stages={stages} />;
    }

    // F95: the yield calculator. The document declares DATA (who comes, the
    // benchmark ranges, the tariffs of the offer); every behaviour is the
    // component's. An `<aud>` without its three rates is dropped rather than
    // defaulted: a made-up conversion is worse than one audience fewer.
    case "calc": {
      const audiences: DocCalcAudience[] = childrenOf(node, "aud").flatMap((entry, index) => {
        const reg = parseCalcRange(entry.attrs.reg);
        const show = parseCalcRange(entry.attrs.show);
        const buy = parseCalcRange(entry.attrs.buy);
        if (!reg || !show || !buy) return [];
        const ads = entry.attrs.ads !== undefined
          && !["false", "ne", "0"].includes((entry.attrs.ads || "").toLowerCase());
        return [{
          key: (entry.attrs.key ?? `a${index + 1}`).trim() || `a${index + 1}`,
          name: entry.attrs.name ?? "",
          note: entry.attrs.note ?? docText(entry.children),
          reg, show, buy, ads,
          cpv: Number((entry.attrs.cpv ?? "").replace(",", ".")) || 0
        }];
      });
      const tariffs: DocCalcTariff[] = childrenOf(node, "tariff").flatMap((entry) => {
        const price = Number((entry.attrs.price ?? "").replace(/\s/g, "").replace(",", "."));
        if (!Number.isFinite(price) || price <= 0) return [];
        return [{
          name: entry.attrs.name ?? "",
          price,
          recommended: entry.attrs.mark !== undefined && entry.attrs.mark !== "false"
        }];
      });
      const rest = node.children.filter(
        (child) => child.kind !== "el" || !["aud", "tariff"].includes(child.tag)
      );
      return (
        <DocCalc
          key={key}
          title={attrs.title}
          visits={Number((attrs.visits ?? "").replace(/\s/g, "")) || 0}
          currency={attrs.currency || docT("doc.calc.currency")}
          audiences={audiences}
          tariffs={tariffs}
        >
          {rest.length > 0 ? renderNodes(rest, ctx, `${key}-c`) : null}
        </DocCalc>
      );
    }

    case "aud":
    case "tariff":
      // Only meaningful inside <calc>; a stray one keeps whatever it said.
      return renderBody(node, ctx, key);

    // F91: a page in miniature with the REAL copy in the real hierarchy.
    // The wf-* blocks render in authored order; an unknown child keeps its
    // words through the ordinary walk, so nothing a model wrote vanishes.
    case "wireframe": {
      const blocks = node.children.map((child, index) => {
        const blockKey = `${key}-w${index}`;
        if (child.kind !== "el") return renderNode(child, ctx, blockKey);
        switch (child.tag) {
          case "wf-pretitle":
            return <p key={blockKey} className="doc-wf-pre">{renderInline(child.children, ctx, blockKey)}</p>;
          case "wf-title":
            return <p key={blockKey} className="doc-wf-h1">{renderInline(child.children, ctx, blockKey)}</p>;
          case "wf-sub":
            return <p key={blockKey} className="doc-wf-sub">{renderInline(child.children, ctx, blockKey)}</p>;
          case "wf-bullets":
            return (
              <ul key={blockKey} className="doc-wf-bullets">
                {childrenOf(child, "item").map((item, itemIndex) => (
                  <li key={`${blockKey}-i${itemIndex}`}>{renderInline(item.children, ctx, `${blockKey}-i${itemIndex}`)}</li>
                ))}
              </ul>
            );
          case "wf-form": {
            const fields = (child.attrs.fields ?? "").split("|").map((field) => field.trim()).filter(Boolean);
            return (
              <div key={blockKey}>
                <DocWfForm fields={fields} button={child.attrs.button} note={child.attrs.note} />
                {child.children.length > 0 && renderNodes(child.children, ctx, blockKey)}
              </div>
            );
          }
          case "wf-video":
            return <div key={blockKey} className="doc-wf-video">{child.attrs.label ?? docText(child.children)}</div>;
          case "wf-cta":
            return <span key={blockKey} className="doc-wf-cta">{renderInline(child.children, ctx, blockKey)}</span>;
          case "wf-note":
            return <p key={blockKey} className="doc-wf-small">{renderInline(child.children, ctx, blockKey)}</p>;
          default:
            return renderNode(child, ctx, blockKey);
        }
      });
      return (
        <DocWireframe
          key={key}
          id={safeDocId(attrs.id)}
          kind={attrs.kind ?? "reg"}
          name={attrs.name}
          note={attrs.note}
        >
          {blocks}
        </DocWireframe>
      );
    }

    // F93: the evening in two levels, both inside ONE block. The map and the
    // phase panels render from the SAME <ph> nodes (a dead map row cannot be
    // authored), and the panel is where the phase's minute axis OPENS - Pavel
    // 2026-08-07: "musi to byt klikatelny. Otvirat mi to ty odrazky."
    case "evening": {
      const phNodes = childrenOf(node, "ph");
      if (phNodes.length === 0) return renderBody(node, ctx, key);
      const phases: DocEveningPhase[] = phNodes.map((ph, index) => {
        const n = (ph.attrs.n ?? String(index + 1)).trim();
        // The depth signal on the map row: how many minute steps wait inside.
        const steps = childrenOf(ph, "minutes")
          .reduce((count, block) => count + childrenOf(block, "mi").length, 0);
        return {
          id: phaseCardId(n),
          n,
          name: ph.attrs.name ?? "",
          start: ph.attrs.start ?? "",
          minutes: parseMinutes(ph.attrs.min),
          minLabel: minuteLabel(ph.attrs.min),
          steps,
          body: renderNodes(ph.children, ctx, `${key}-p${index}`)
        };
      });
      // Anything the model wrote beside the phases keeps its words.
      const rest = node.children.filter((child) => child.kind !== "el" || child.tag !== "ph");
      return (
        <div key={key} className="doc-evening-block">
          <DocEveningBlock title={attrs.title} total={attrs.total} phases={phases} />
          {rest.length > 0 && renderNodes(rest, ctx, `${key}-rest`)}
        </div>
      );
    }

    // F93: the minute axis inside one phase - what to say, minute by minute.
    // `eg` carries ONE concrete example per step: the wording is the owner's
    // to replace, the structure is not. Only rows with content render.
    case "minutes": {
      const rows: DocMinuteRow[] = childrenOf(node, "mi").map((mi, index) => ({
        at: mi.attrs.at ?? "",
        body: renderInline(mi.children, ctx, `${key}-m${index}`),
        example: mi.attrs.eg
      }));
      if (rows.length === 0) return null;
      return <DocMinutes key={key} title={attrs.title} rows={rows} />;
    }

    // F93: the seam between phases - what must already have been said, and
    // what this phase sets up. Attributes, so a seam can never grow into an
    // essay inside the phase body.
    case "seam":
      return <DocSeam key={key} before={attrs.before} after={attrs.after} />;

    // F93: the story arc of the whole evening as its own short element.
    case "arc": {
      const moves: DocArcMove[] = childrenOf(node, "move").map((move, index) => ({
        n: (move.attrs.n ?? String(index + 1)).trim(),
        label: move.attrs.label ?? "",
        body: renderInline(move.children, ctx, `${key}-a${index}`)
      }));
      if (moves.length === 0) return renderBody(node, ctx, key);
      return <DocArc key={key} title={attrs.title} moves={moves} />;
    }

    case "move":
      // Only meaningful inside <arc>; a stray one keeps its words.
      return isInlineOnly(node.children) && node.children.length > 0
        ? <p key={key}>{renderInline(node.children, ctx, key)}</p>
        : null;

    // F93: the promise scope. Pavel 2026-08-07: the evening's name and promise
    // are derived from what the PRODUCT covers and must match the offer's
    // promise, or the room fills with people who want one symptom solved and
    // will never buy. Rendering the covered areas as chips puts the law on the
    // page, not only in the gate.
    case "scope": {
      const covers = (attrs.covers ?? "").split("|").map((area) => area.trim()).filter(Boolean);
      return (
        <DocScope key={key} title={attrs.title} offer={attrs.offer} covers={covers}>
          {renderNodes(node.children, ctx, key)}
        </DocScope>
      );
    }

    case "table": {
      const head = (attrs.head ?? "").split("|").map((cell) => cell.trim()).filter(Boolean);
      const rows = childrenOf(node, "row").map((row) => ({
        cells: docText(row.children).split("|").map((cell) => cell.trim()),
        // The recommended tier gets its own attribute so the highlight is
        // structure, not a CSS text-match on "Doporučujeme".
        marked: row.attrs.mark !== undefined && row.attrs.mark !== "false",
        // A schedule table is the timeline in table form; carrying the segment
        // colour here is what makes the two read as one document.
        seg: segmentClass(ctx.segments, row.attrs.seg)
      }));
      return (
        <div key={key} className="doc-table-wrap">
          <table className="doc-table">
            {head.length > 0 && (
              <thead>
                <tr>{head.map((cell, index) => <th key={`${key}-h${index}`}>{cell}</th>)}</tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`${key}-r${rowIndex}`} className={[row.marked ? "doc-row-mark" : "", row.seg ?? ""].filter(Boolean).join(" ") || undefined}>
                  {row.cells.map((cell, cellIndex) => <td key={`${key}-c${cellIndex}`}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "list":
    case "check": {
      const checklist = node.tag === "check";
      return (
        <ul key={key} className={checklist ? "doc-checklist" : "doc-list"}>
          {childrenOf(node, "item").map((item, index) => {
            const done = item.attrs.done !== undefined && item.attrs.done !== "false";
            return (
              <li key={`${key}-i${index}`} className={checklist ? `doc-check${done ? " doc-check-done" : ""}` : undefined}>
                {checklist && <span className="doc-check-box" aria-hidden="true">{done ? "✓" : ""}</span>}
                <span className={checklist ? "doc-check-text" : undefined}>
                  {renderInline(item.children, ctx, `${key}-i${index}`)}
                </span>
              </li>
            );
          })}
        </ul>
      );
    }

    case "divider":
      return attrs.kicker
        ? <p key={key} className="doc-divider-kicker">{attrs.kicker}</p>
        : <hr key={key} className="doc-divider" />;

    default:
      // Unknown tag: keep the content, drop the tag. Never a blank space where
      // the model wrote something, never an executed unknown.
      return isInlineOnly(node.children)
        ? <p key={key}>{renderInline(node.children, ctx, key)}</p>
        : <>{renderNodes(node.children, ctx, key)}</>;
  }
}

/**
 * `docContext` matters only for FRAGMENTS. The e-mails view renders the intro
 * and each e-mail separately, so the fragment holding the timeline no longer
 * contains the folds, and a fold no longer contains the `<seg>` legend. The
 * splitter builds the context from the whole source once and passes it in;
 * a whole document derives its own.
 */
export function DocHtmlView(props: { html: string; sectionStart?: number; docContext?: DocContext }) {
  const content = useMemo(() => {
    const nodes = parseDocHtml(props.html);
    const shared = props.docContext ?? docContextFromNodes(nodes);
    const ctx: RenderCtx = {
      chapter: { n: props.sectionStart ?? 0 },
      segments: shared.segments,
      foldIds: new Set(shared.foldIds)
    };
    return renderNodes(nodes, ctx, "d");
  }, [props.html, props.sectionStart, props.docContext]);
  return <>{content}</>;
}
